import React, {
    PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { HubConnection } from '@microsoft/signalr';
import i18next from 'i18next';
import { IConnectionContext } from './interfaces/IConnectionContext';
import NotificationManager from '../../../common/services/notification-manager';

export const ConnectionContext = React.createContext<IConnectionContext>(
    {} as IConnectionContext,
);

export interface ConnectionProviderProps extends PropsWithChildren {
    group: string;
    sulaHubId?: string;
}

export function ConnectionProvider({
    children,
    group,
    sulaHubId,
}: Readonly<ConnectionProviderProps>) {
    const connectionRef = useRef<HubConnection | null>(null);
    const [connection, setConnection] = useState<HubConnection | null>(null);
    const [connectionState, setConnectionState] = useState('Disconnected');
    const [isConnected, setIsConnected] = useState(false);
    const [error, setError] = useState<string | undefined>(undefined);

    const connect = useCallback(async () => {
        const newConnection = await NotificationManager.getConnectionManager(
            group,
            sulaHubId,
        );

        connectionRef.current = newConnection;

        newConnection.onclose(() => {
            setConnectionState('Disconnected');
            setIsConnected(false);
        });

        newConnection.onreconnecting(() => {
            setConnectionState('Reconnecting');
            setIsConnected(false);
        });

        newConnection.onreconnected(() => {
            setConnectionState('Connected');
            setIsConnected(true);
        });

        try {
            setConnectionState('Connecting');

            await newConnection.start();

            setConnectionState('Connected');
            setIsConnected(true);
            setConnection(newConnection);
        } catch {
            setConnectionState('Disconnected');
            setIsConnected(false);
            setError(i18next.t('UpdatesDescription'));
        }
    }, [group, sulaHubId]);

    const disconnect = useCallback(async () => {
        const conn = connectionRef.current;
        if (
            conn &&
            ['Connected', 'Reconnecting', 'Connecting'].includes(conn.state)
        ) {
            await conn.stop();
        }
    }, []);

    useEffect(() => {
        connect();

        return () => {
            disconnect();
        };
    }, [connect, disconnect]);

    const value = useMemo(
        () => ({
            connection,
            error,
            connectionState,
            isConnected,
        }),
        [connection, error, connectionState, isConnected],
    );

    return (
        <ConnectionContext.Provider value={value}>
            {children}
        </ConnectionContext.Provider>
    );
}

export const useConnection = (): IConnectionContext =>
    useContext(ConnectionContext);
